import liff from '@line/liff'
import { get } from 'lodash-es'
import { createRouter, createWebHistory } from 'vue-router'

import { MAPPING_ROUTE_OBJECT } from '@/constants/mappingRouteObject'
import store from '@/store'
import {
  getSessionStorage,
  recordInitialReferrer,
  removeUserLocalStorage,
  sendDatadogLog,
} from '@/utils'
import { getNow } from '@/utils/timeFormat'

// Containers
const TheContainer = () => import('@/views/containers/TheContainer.vue')

// Views - Pages
const Booking = () => import('@/views/pages/Booking.vue')
const BookingDetail = () => import('@/views/pages/BookingDetail.vue')
const VoucherDetail = () => import('@/views/pages/VoucherDetail.vue')
const FormDetail = () => import('@/views/pages/FormDetail.vue')
const Checkout = () => import('@/views/pages/Checkout.vue')
const Payment = () => import('@/views/pages/Payment.vue')
const Record = () => import('@/views/pages/Record.vue')
const RecordDetail = () => import('@/views/pages/RecordDetail.vue')
const Store = () => import('@/views/pages/Store.vue')
const StoreMenu = () => import('@/views/pages/StoreMenu.vue')
const StoreRetail = () => import('@/views/pages/retail/StoreRetail.vue')
const RetailMenuDetail = () =>
  import('@/views/pages/retail/RetailMenuDetail.vue')
const RetailCheckout = () => import('@/views/pages/retail/RetailCheckout.vue')
const RetailOrderDetail = () =>
  import('@/views/pages/retail/RetailOrderDetail.vue')
const UserLogin = () => import('@/views/pages/UserLogin.vue')
const UserProfile = () => import('@/views/pages/UserProfile.vue')
const ProfileEditor = () => import('@/views/pages/ProfileEditor.vue')
const UserVoucher = () => import('@/views/pages/UserVoucher.vue')
const UserValueCard = () => import('@/views/pages/UserValueCard.vue')
const Terms = () => import('@/views/pages/Terms.vue')
const NotFound = () => import('@/views/pages/NotFound.vue')
const Message = () => import('@/views/pages/Message.vue')
const ElementPage = () => import('@/views/pages/ElementPage.vue')
const LoginPassword = () => import('@/views/pages/LoginPassword.vue')
const SetPassword = () => import('@/views/pages/SetPassword.vue')
const ForgetPassword = () => import('@/views/pages/ForgetPassword.vue')
const Register = () => import('@/views/pages/Register.vue')
const RegisterProfile = () => import('@/views/pages/RegisterProfile.vue')
const LiffRedirectPage = () => import('@/views/pages/LiffRedirectPage.vue')

function configRoutes() {
  return [
    { path: '/:pathMatch(.*)*', name: 'NotFound', component: NotFound },
    ...(import.meta.env.DEV
      ? [
          {
            path: '/elementUI',
            name: 'ElementUI',
            component: ElementPage,
          },
        ]
      : []),
    {
      path: '/',
      component: TheContainer,
      children: [
        {
          path: '',
          name: 'LiffRedirect',
          meta: {
            label: 'LIFF中轉頁',
            passAuth: false,
          },
          component: LiffRedirectPage,
        },
        {
          path: 'login',
          name: 'UserLogin',
          meta: {
            label: '登入',
            passAuth: false,
          },
          component: UserLogin,
        },
        {
          path: 'loginPwd',
          name: 'LoginPassword',
          meta: {
            label: '密碼登入',
            passAuth: false,
            needLoginSession: true,
          },
          component: LoginPassword,
        },
        {
          path: 'setPwd',
          name: 'SetPassword',
          meta: {
            label: '註冊密碼',
            passAuth: false,
            needLoginSession: true,
          },
          component: SetPassword,
        },
        {
          path: 'forgetPwd',
          name: 'ForgetPassword',
          meta: {
            label: '忘記密碼',
            passAuth: false,
          },
          component: ForgetPassword,
        },
        {
          path: 'register',
          name: 'Register',
          meta: {
            label: '註冊',
            passAuth: false,
            needLoginSession: true,
          },
          component: Register,
        },
        {
          path: 'registerProfile',
          name: 'RegisterProfile',
          meta: {
            label: '註冊時填寫資料',
            passAuth: false,
            needLoginSession: true,
          },
          component: RegisterProfile,
        },
        {
          path: 'terms',
          name: 'Terms',
          meta: {
            label: '服務條款',
            passAuth: false,
          },
          component: Terms,
        },
        {
          path: 'store',
          name: 'Store',
          meta: {
            label: '商店入口',
            passAuth: false,
          },
          component: Store,
        },
        {
          path: 'menu/:pid?',
          name: 'StoreMenu',
          meta: {
            label: '服務列表',
            passAuth: false,
          },
          component: StoreMenu,
        },
        {
          path: 'retail',
          name: 'StoreRetail',
          meta: {
            label: '商品列表',
            passAuth: false,
          },
          component: StoreRetail,
        },
        {
          path: 'merchandise',
          name: 'RetailMenuDetail',
          meta: {
            label: '商品詳細列表',
            passAuth: false,
          },
          component: RetailMenuDetail,
        },
        {
          path: 'booking',
          name: 'Booking',
          meta: {
            label: '預約',
            passAuth: false,
          },
          component: Booking,
        },
        {
          path: 'payment/retail',
          name: 'Payment',
          meta: {
            label: '付款',
            passAuth: true,
          },
          component: Payment,
        },
        {
          path: 'checkout',
          name: 'Checkout',
          meta: {
            label: '結帳',
            passAuth: true,
          },
          component: Checkout,
        },
        {
          path: 'buy',
          name: 'Buy',
          meta: {
            label: '商品結帳',
            passAuth: true,
          },
          component: RetailCheckout,
        },
        {
          path: 'record',
          name: 'Record',
          meta: {
            label: '我的紀錄',
            passAuth: true,
          },
          component: Record,
        },
        {
          path: 'detail',
          name: 'RecordDetail',
          meta: {
            label: '交易紀錄',
            passAuth: true,
          },
          component: RecordDetail,
        },
        {
          path: 'booking/:bookingId',
          name: 'BookingDetail',
          meta: {
            label: '預約服務詳情',
            passAuth: false,
          },
          component: BookingDetail,
        },
        {
          path: 'pickupOrder/:pickId',
          name: 'RetailOrderDetail',
          meta: {
            label: '商品訂購詳情',
            passAuth: false,
          },
          component: RetailOrderDetail,
        },
        {
          path: 'user',
          meta: {
            label: '使用者',
            passAuth: true,
          },
          component: {
            render() {
              return h(resolveComponent('router-view'))
            },
          },
          children: [
            {
              path: '',
              redirect: '/user/profile',
            },
            {
              path: 'profile',
              name: 'UserProfile',
              meta: {
                label: '使用者資訊',
              },
              component: UserProfile,
            },
            {
              path: 'profile/editor',
              name: 'ProfileEditor',
              meta: {
                label: '編輯顧客資訊',
              },
              component: ProfileEditor,
            },
            {
              path: 'voucher',
              name: 'UserVoucher',
              meta: {
                label: '優惠券列表',
              },
              component: UserVoucher,
            },
            {
              path: 'valueCard',
              name: 'UserValueCard',
              meta: {
                label: '我的票卡',
              },
              component: UserValueCard,
            },
          ],
        },
        {
          path: 'message',
          name: 'Message',
          meta: {
            label: '訊息顯示',
            passAuth: false,
          },
          component: Message,
        },
        {
          path: 'voucher/:voucherId',
          name: 'VoucherDetail',
          meta: {
            label: '優惠券詳情',
            passAuth: false,
          },
          component: VoucherDetail,
        },
        {
          path: 'form/:formId',
          name: 'FormDetail',
          meta: {
            label: '單一表單內容',
            passAuth: false,
          },
          component: FormDetail,
        },
      ],
    },
  ]
}

const router = createRouter({
  history: createWebHistory(),
  linkActiveClass: 'active',
  scrollBehavior: () => ({ top: 0 }),
  routes: configRoutes(),
})

router.beforeEach(async (to, from) => {
  store.commit('store/clearGetStore')
  recordInitialReferrer()
  if (to.fullPath.includes('liff.state') && !liff.isInClient()) {
    // NOTE: 從 liff.line.me / LINE Menu 跳轉過來的情境且非 LIFF 瀏覽器
    const liffInitURL = decodeURIComponent(to.fullPath)
      .split('?liff.state=')[1]
      .replace(/\/\//g, '/')
    const liffURL = new URL(
      `${import.meta.env.VITE_APP_Booking_URL}${liffInitURL}`,
    )

    const searchURL = new URLSearchParams(new URL(liffURL).search)
    const queryObject = {
      cname: searchURL.get('cname'),
      ...(searchURL.has('userId') ? { userId: searchURL.get('userId') } : null),
      ...(searchURL.has('origin') ? { origin: searchURL.get('origin') } : null),
    }
    return {
      name: searchURL.has('redirectUri')
        ? MAPPING_ROUTE_OBJECT[searchURL.get('redirectUri')]
        : 'Store',
      query: queryObject,
      replace: true,
    }
  } else {
    const needPassAuth = to.matched.some(record => record.meta.passAuth)
    const localStorageData = localStorage.getItem('QLiEERBeautyBooking-User')
    const authData = JSON.parse(localStorageData)
    const storeCname = to.query?.cname || ''
    let authToken =
      authData && authData[storeCname] ? authData[storeCname].token : null
    let isLoggedIn = authToken !== null

    // 防止 cname 不存在時候會無限重整頁面的問題
    if (!liff.isInClient() && !storeCname && to.name !== 'NotFound') {
      return { name: 'NotFound', query: to.query, replace: true }
    }

    // token 效期為 30 天，低於 29 天效期時進行 renew，舊有 token 會失效
    if (authToken) {
      const expireTime = authData[storeCname].expireTime
      const now = getNow({ type: 'UnixTime' })
      if (expireTime - now <= 29 * 86400) {
        const result = await store.dispatch('auth/renewToken', {
          code: storeCname,
        })
        if (result.status !== 'SUCCESS') {
          removeUserLocalStorage({ cname: storeCname })
          isLoggedIn = false
          authToken = null
        }
      } else {
        store.dispatch('auth/autoLogin', { code: storeCname })
      }
    }

    // 不需登入頁面
    if (!needPassAuth) {
      const needLoginSession = to.matched.some(
        record => record.meta.needLoginSession,
      )
      const loginSessionData = getSessionStorage(`Login-${storeCname}`)
      if (needLoginSession) {
        // 只有忘記密碼頁例外，需進入該頁面判斷跳轉至何處
        return loginSessionData
          ? true
          : { name: 'UserLogin', query: to.query, replace: true }
      }
      return true
    }

    // NOTE: 如遇到 needPassAuth = true 的頁面要檢查，跳轉頁面用的參數是否都在 Query 上
    if (needPassAuth && !isLoggedIn) {
      // 需登入頁面且未登入
      const targetKey = Object.keys(MAPPING_ROUTE_OBJECT).find(
        key => MAPPING_ROUTE_OBJECT[key] === to.name,
      )
      return {
        name: 'UserLogin',
        query: {
          ...(targetKey ? { redirectUri: targetKey } : {}),
          ...(targetKey && targetKey === 'profile'
            ? {
                from: Object.keys(MAPPING_ROUTE_OBJECT).find(
                  key => MAPPING_ROUTE_OBJECT[key] === from.name,
                ),
              }
            : {}),
          ...to.query,
        },
        replace: true,
      }
    } else {
      // 需登入頁面且已登入
      return true
    }
  }
})

router.onError((error, to) => {
  const reloadErrors = [
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
  ]
  sendDatadogLog({
    message: 'test router error',
    data: { error, to },
    status: 'debug',
  })
  if (reloadErrors.some(value => error.message.includes(value))) {
    window.location.href = to.fullPath
  } else {
    const liffQuery = get(to.query, 'liff.state', '')
    if (liffQuery) {
      sendDatadogLog({
        message: '有 liff 並發生解譯錯誤',
        data: { error, to },
        status: 'debug',
      })
      window.location.href = liffQuery.split('#')[0]
    }
  }
})

export default router
